/**
 * Delete a demo, after saying exactly what goes with it.
 *
 * ON BOTH DEMO PAGES, NOT THE LIST. A row on the list is one whole link, and a demo that has
 * never been published only ever reaches Review - so the header of each page is the one
 * place every demo, in every state, can be deleted from.
 *
 * THE QUESTION NAMES THE COST. A live link that stops working and a prospect's recorded
 * calls are the two things somebody would be sorry about, so the sentence says them in
 * numbers rather than asking "are you sure?" of nothing in particular.
 *
 * BACK TO THE LIST ON SUCCESS. Leaving the page is also what stops its poll, which would
 * otherwise go on asking for a demo that is no longer there.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, del } from "./api";
import type { Demo } from "./types";
import { Button, CardHead, Modal } from "./ui";

export function DeleteDemo({ demo }: { demo: Demo }) {
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = `delete-${demo.id}-title`;
  const name = demo.business_name || demo.website_url.replace(/^https?:\/\//, "");

  function close() {
    if (deleting) return;
    setAsking(false);
    setError(null);
  }

  async function confirm() {
    setDeleting(true);
    setError(null);
    try {
      await del(`/v1/demos/${demo.id}`);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the demo.");
      setDeleting(false);
    }
  }

  const goes = [
    demo.published_spec ? <>The link <span className="font-mono text-[13px]">/call/{demo.slug}</span> stops working</> : null,
    demo.calls ? <>{demo.calls} call{demo.calls === 1 ? "" : "s"} and {demo.calls === 1 ? "its recording" : "their recordings"} are removed</> : null,
  ].filter(Boolean);

  return (
    <>
      <Button kind="danger" onClick={() => setAsking(true)}>Delete</Button>
      {asking && (
        <Modal onClose={close} labelledBy={titleId} width="w-[min(480px,calc(100vw-2rem))]">
          <CardHead title={<span id={titleId}>Delete {name}?</span>} />
          <div className="flex flex-col gap-3 px-5 py-4 text-[14px] leading-relaxed">
            {goes.length > 0 && (
              <ul className="m-0 pl-5 list-disc flex flex-col gap-1">
                {goes.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            )}
            <div className="text-muted">This can't be undone.</div>
            {error && <div className="px-4 py-2.5 bg-warn-wash text-warn rounded-lg text-[13px]">{error}</div>}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-line-soft">
            <Button kind="quiet" onClick={close} disabled={deleting}>Cancel</Button>
            <Button kind="danger" onClick={() => void confirm()} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete demo"}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
